import type { StreamFailure } from "@shared/player/streamPlayback";

/**
 * The zap machine (`TV-4`) — and the decoder ceiling it exists to respect.
 *
 * ## ⚠️ The number that decides whether guests accept this feature
 *
 * A guest compares a channel change to a broadcast tuner. HLS/DASH over MSE
 * with a DRM licence round-trip is not that: a cold load is a manifest fetch, an
 * init segment, a CDM session, a licence round trip and a decoder handshake
 * before the first frame. So the latency is designed for HERE, not discovered
 * later:
 *
 * | case | target, keypress → first frame |
 * |---|---|
 * | the pre-warmed neighbour | **≤ 1200 ms** |
 * | anywhere else (cold) | **≤ 2500 ms** |
 *
 * Both are measured for real — `GuideScreen` times every change and reports it
 * through `playbackTelemetry` (`playbackZapped`). ⚠️ **These are targets, not
 * measurements: nothing here has been timed on a MediaSuite panel yet.** If the
 * fleet's numbers come back past them, the honest outcome is that live TV stays
 * on the vendor app while VOD moves — which is exactly what the premise-level
 * player switch makes expressible. Writing the finding down beats hiding it.
 *
 * Three mechanisms buy the difference:
 *
 *  1. **One pre-warmed spare.** The neighbour in the direction of travel is
 *     loaded, muted, behind the live surface. Zapping onto it is a promotion,
 *     not a load — the element is already decoding, so first frame is already
 *     there.
 *  2. **A settle window.** Holding ch+ must not mount and tear down a player per
 *     press. The bar follows every press instantly; the tune commits
 *     `TUNE_SETTLE_MS` after the last one — except onto the spare, which costs
 *     nothing and commits at once.
 *  3. **A poster over the gap.** The programme's own artwork with the channel
 *     name, so the screen is never black and never a spinner.
 *
 * ## 🚨 Why the spare is ONE, and never a pool
 *
 * A hotel panel's hardware H.264 decoders are a pooled resource in the single
 * digits, and an Android WebView does **not** hand one back when a `<video>`
 * leaves the DOM ([[panel-video-encoding]]). Signage learned this expensively
 * and settled on "two layers, never more". This machine holds the same ceiling:
 * {@link mountedSurfaces} returns **at most two ids**, and it is the only thing
 * `GuideScreen` renders players from, so a third surface cannot be mounted by
 * accident. `StreamSurface` releases explicitly on unmount (pause,
 * `removeAttribute("src")`, `load()`), which is the other half of the deal.
 *
 * And if a panel tells us it ran out anyway — `decoder_exhausted` — the spare is
 * given up **for the rest of the session** ({@link zapReducer} `failed`). A
 * device that cannot afford two surfaces gets one, slowly, rather than a screen
 * that stops playing anything at all.
 *
 * ## ⚠️ Known cost of the spare: it is counted as playback
 *
 * `StreamSurface` calls `playbackStarted()` / `playbackPlaying()` /
 * `playbackFailed()` unconditionally, so a warmed neighbour adds a `started`
 * and a `playing` for a channel nobody watched — which distorts the
 * `started - playing` "silent gap" figure `playbackTelemetry` documents — and a
 * spare that cannot resolve a licence writes its failure code into the tally
 * even though the guest is never told. The fix is one optional `prewarm` prop on
 * `StreamSurface` that suppresses its telemetry calls; that file belongs to
 * `PLAYER-9` and this unit deliberately does not edit it. Until then, read
 * `started`/`playing` on a room with live TV as "loads", not "titles watched".
 */

/** How long the tune waits for the guest to stop pressing. */
export const TUNE_SETTLE_MS = 300;
/**
 * How long a settled channel must play before its neighbour is warmed.
 *
 * Long enough that browsing past six channels warms nothing, short enough that a
 * guest who is genuinely walking the line-up gets the fast path from the second
 * press onward.
 */
export const PREWARM_AFTER_MS = 2_500;
/**
 * When a channel that never reached a frame stops being "still loading" and
 * starts being a fault with a name.
 *
 * `StreamSurface`'s own watchdog only starts once playback has begun, so a load
 * that hangs forever produces no error at all. Something has to say so:
 * a spinner that never resolves is the failure mode this whole series exists to
 * remove.
 */
export const TUNE_TIMEOUT_MS = 12_000;

export interface ZapState {
  /** The channel on screen and unmuted. */
  live: string | null;
  /** The pre-warmed neighbour, muted and hidden. At most one, ever. */
  spare: string | null;
  /** Where the guest was before, for the last-channel toggle. */
  prev: string | null;
  /** Aimed at, shown in the bar, not yet committed to a player. */
  pending: string | null;
  /** Ids whose surface has reached a first frame. Pruned to what is mounted. */
  playing: string[];
  /** A panel that reported `decoder_exhausted` never gets a spare again. */
  prewarmAllowed: boolean;
}

export type ZapAction =
  /** Land on a channel with no transition — the first channel of a session. */
  | { type: "land"; id: string }
  /** Aim at a channel. The bar follows this; the player does not, yet. */
  | { type: "aim"; id: string }
  /** The settle window closed (or the target was the spare). Tune. */
  | { type: "commit" }
  /** Warm a neighbour. Ignored while a tune is in flight. */
  | { type: "warm"; id: string }
  /** Give the spare back — a mode change, a failure, the screen closing. */
  | { type: "cool" }
  /** A surface reached its first frame. */
  | { type: "playing"; id: string }
  /** A surface failed, by name. */
  | { type: "failed"; id: string; failure: StreamFailure };

export function initialZap(): ZapState {
  return {
    live: null,
    spare: null,
    prev: null,
    pending: null,
    playing: [],
    prewarmAllowed: true,
  };
}

/**
 * The surfaces `GuideScreen` may mount, in render order (live last, on top).
 *
 * **This is the ceiling.** Two ids at most, deduplicated, and nothing else in
 * the screen is allowed to mount a player.
 */
export function mountedSurfaces(state: ZapState): string[] {
  const out: string[] = [];
  if (state.spare && state.spare !== state.live) out.push(state.spare);
  if (state.live) out.push(state.live);
  return out;
}

/** Keep only the ids that still have a surface behind them. */
function prune(playing: string[], state: ZapState): string[] {
  const mounted = new Set(mountedSurfaces(state));
  return playing.filter((id) => mounted.has(id));
}

export function zapReducer(state: ZapState, action: ZapAction): ZapState {
  switch (action.type) {
    case "land": {
      if (state.live === action.id) return state;
      const next = { ...state, live: action.id, pending: null, spare: null };
      return { ...next, playing: prune(state.playing, next) };
    }
    case "aim": {
      // Aiming back at what is already playing simply cancels a pending change.
      if (action.id === state.live) {
        return state.pending === null ? state : { ...state, pending: null };
      }
      return { ...state, pending: action.id };
    }
    case "commit": {
      const target = state.pending;
      if (!target || target === state.live) {
        return state.pending === null ? state : { ...state, pending: null };
      }
      const next: ZapState = {
        ...state,
        // The promotion case is the fast one: `target === spare` means that
        // surface is already mounted and (usually) already decoding, so this
        // reducer changes which one is unmuted and mounts nothing.
        live: target,
        spare: null,
        prev: state.live,
        pending: null,
        playing: state.playing,
      };
      return { ...next, playing: prune(next.playing, next) };
    }
    case "warm": {
      // Never while a change is in flight, never onto the live channel, and
      // never on a panel that has already run out of decoders.
      if (!state.prewarmAllowed) return state;
      if (state.pending || !state.live) return state;
      if (action.id === state.live || action.id === state.spare) return state;
      const next = { ...state, spare: action.id };
      return { ...next, playing: prune(state.playing, next) };
    }
    case "cool": {
      if (!state.spare) return state;
      const next = { ...state, spare: null };
      return { ...next, playing: prune(state.playing, next) };
    }
    case "playing": {
      if (state.playing.includes(action.id)) return state;
      return { ...state, playing: prune([...state.playing, action.id], state) };
    }
    case "failed": {
      // The panel is telling us the pool is empty. Believe it: give the spare
      // back and stop asking for one. Zapping gets slower and keeps working,
      // which is the right way round.
      if (action.failure === "decoder_exhausted") {
        const next = { ...state, spare: null, prewarmAllowed: false };
        return { ...next, playing: prune(state.playing, next) };
      }
      // Anything else on the SPARE is the spare's problem alone — the guest is
      // watching something else and is not told about it. (The tally still
      // counts it; see the header.)
      if (action.id === state.spare) {
        const next = { ...state, spare: null };
        return { ...next, playing: prune(state.playing, next) };
      }
      return state;
    }
    default:
      return state;
  }
}

/** What the bar and the poster describe: the aim if there is one, else live. */
export function shownChannel(state: ZapState): string | null {
  return state.pending ?? state.live;
}

/** True while the live surface has not yet produced a frame. */
export function tuning(state: ZapState): boolean {
  if (state.pending && state.pending !== state.live) return true;
  return !!state.live && !state.playing.includes(state.live);
}
